import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, XCircle, RefreshCw, CheckCircle2, Clock, Mail, Smartphone, ArrowLeft, RotateCcw, HandHeart, Building2, Gift, CalendarClock, MapPin, Lock, Phone } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { api, formatDateTime } from "../lib/api";
import { basarGiftStatusMeta, basarUrl, drawDateTime, giftKindLabel, nok, shortTime } from "../lib/basar";
import { useLang } from "../context/LanguageContext";

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 100; // ~5 min

// Statusside for ei gåve («Gi premie»): /basar/gave/:giftId – Vipps-polling for pengegåver, takk/stadfesting for tinggåver.
// Berre givaren sine eigne opplysningar – gåva er aldri lista offentleg.
export default function BasarGiftPage() {
  const { giftId } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const pollCount = useRef(0);
  const stopped = useRef(false);

  const loadGift = () => api.get(`/basar/gifts/${giftId}`).then((res) => setData(res.data));

  useEffect(() => {
    loadGift().catch((err) => {
      if (err?.response?.status === 404) setNotFound(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftId]);

  const pending = data?.gift && ["pending_payment", "processing"].includes(data.gift.status);

  useEffect(() => {
    if (!pending) return undefined;
    stopped.current = false;
    pollCount.current = 0;
    const poll = async () => {
      if (stopped.current) return;
      try {
        const { data: st } = await api.get(`/basar/gifts/${giftId}/payment-status`);
        if (stopped.current) return;
        if (st.status !== "pending") {
          stopped.current = true;
          await loadGift();
          return;
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          setNotFound(true);
          stopped.current = true;
          return;
        }
      }
      pollCount.current += 1;
      if (pollCount.current >= MAX_POLLS) {
        setTimedOut(true);
        stopped.current = true;
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => {
      stopped.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, giftId]);

  if (notFound) {
    return (
      <PublicLayout>
        <div className="max-w-lg mx-auto px-4 py-24 text-center space-y-4" data-testid="basar-gift-not-found">
          <XCircle className="w-10 h-10 mx-auto text-destructive" />
          <h1 className="text-xl font-heading font-semibold">{t("Fant ikke gaven")}</h1>
          <p className="text-sm text-muted-foreground">{t("Sjekk at lenken er riktig.")}</p>
          <Button asChild className="mt-2" data-testid="basar-gift-home-button"><Link to="/basar"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Alle basarer")}</Link></Button>
        </div>
      </PublicLayout>
    );
  }

  if (!data) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-10 space-y-4" data-testid="basar-gift-loading"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-48 rounded-2xl" /></div>
      </PublicLayout>
    );
  }

  const { gift, email_enabled: emailEnabled } = data;
  const st = basarGiftStatusMeta(gift);
  const money = gift.kind === "money";
  const done = gift.status === "paid" || gift.status === "registered";
  const basar = gift.basar || {};

  const statusBlock = () => {
    if (pending && timedOut) {
      return (
        <div className="text-center space-y-3" data-testid="basar-gift-timeout">
          <RefreshCw className="w-10 h-10 mx-auto text-muted-foreground" />
          <h2 className="text-lg font-heading font-semibold">{t("Vi venter fortsatt på Vipps")}</h2>
          <p className="text-sm text-muted-foreground">{t("Vi har ikke fått bekreftelse enda. Hvis du har betalt, prøv å oppdatere siden.")}</p>
          <Button onClick={() => window.location.reload()} data-testid="basar-gift-retry-button"><RefreshCw className="w-4 h-4 mr-1.5" />{t("Sjekk på nytt")}</Button>
        </div>
      );
    }
    if (pending) {
      return (
        <div className="text-center space-y-3" data-testid="basar-gift-pending">
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
          <h2 className="text-lg font-heading font-semibold">{t("Venter på Vipps")}</h2>
          <p className="text-sm text-muted-foreground">{t("Fullfør betalingen i Vipps-appen – siden oppdateres automatisk. Gaven blir lagt inn som premie når betalingen er godkjent.")}</p>
          {gift.hold_expires_at && <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid="basar-gift-hold-expires"><Clock className="w-3.5 h-3.5" /> {t("Betalingen må fullføres innen kl. {time}", { time: shortTime(gift.hold_expires_at) })}</p>}
        </div>
      );
    }
    if (done) {
      return (
        <div className="space-y-4" data-testid="basar-gift-done">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"><CheckCircle2 className="w-5 h-5" /></span>
            <div>
              <h2 className="text-lg font-heading font-semibold">{t("Tusen takk for gaven!")}</h2>
              <p className="text-sm text-muted-foreground">{money && gift.paid_at ? t("Betalt {date} med Vipps", { date: formatDateTime(gift.paid_at) }) : t("Registrert {date}", { date: formatDateTime(gift.created_at) })}</p>
            </div>
          </div>
          <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-4 space-y-1.5" data-testid="basar-gift-prize-note">
            <p className="font-heading font-semibold inline-flex items-center gap-2"><Gift className="w-5 h-5 text-[hsl(var(--warning))]" /> {money ? t("Gaven er nå premien «{name}»", { name: gift.name }) : t("«{name}» er nå med i premielisten", { name: gift.name })}</p>
            <p className="text-sm text-muted-foreground">
              {money
                ? t("Beløpet gikk direkte til {org}. Vinneren trekkes sammen med de andre premiene.", { org: gift.organizer_name })
                : basar.contact_info
                  ? t("Arrangøren tar kontakt for å avtale henting. Du kan også kontakte arrangøren selv: {contact}", { contact: basar.contact_info })
                  : t("Arrangøren tar kontakt for å avtale henting.")}
            </p>
          </div>
          <p className="text-xs text-muted-foreground inline-flex items-start gap-1.5" data-testid="basar-gift-privacy-note"><Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {t("Navnet ditt vises ikke offentlig – bare arrangøren ser hvem som har gitt.")}</p>
        </div>
      );
    }
    if (gift.status === "cancelled") {
      return (
        <div className="space-y-3" data-testid="basar-gift-cancelled">
          <div className="flex items-center gap-3"><span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]"><XCircle className="w-5 h-5" /></span><h2 className="text-lg font-heading font-semibold">{t("Betalingen ble avbrutt")}</h2></div>
          <p className="text-sm text-muted-foreground">{t("Ingenting er trukket. Du kan prøve igjen fra basarsiden.")}</p>
          <Button asChild variant="outline" size="sm" data-testid="basar-gift-retry-link"><Link to={basarUrl(gift.basar_id)}><RotateCcw className="w-4 h-4 mr-1.5" /> {t("Tilbake til basaren")}</Link></Button>
        </div>
      );
    }
    return null;
  };

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 py-10 space-y-6" data-testid="basar-gift-page" data-status={gift.status}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground inline-flex items-center gap-1.5"><HandHeart className="w-4 h-4 text-[hsl(var(--warning))]" /> {t("Gaven din")}</p>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="basar-gift-title">{money ? t("Pengegave {amount}", { amount: nok(gift.value) }) : gift.name}{!money && gift.count > 1 ? ` × ${gift.count}` : ""}</h1>
            <p className="text-sm text-muted-foreground mt-1">{giftKindLabel(gift.kind)}{!money && gift.value ? ` · ${t("verdi {amount}", { amount: nok(gift.value) })}` : ""}</p>
          </div>
          <Badge className={`${st.className} shrink-0 self-start`} data-testid="basar-gift-status-badge">{st.label}</Badge>
        </div>

        <Card className="rounded-2xl border-border"><CardContent className="p-6">{statusBlock()}</CardContent></Card>

        <Card className="rounded-2xl border-border" data-testid="basar-gift-summary">
          <CardContent className="p-6 space-y-4">
            <div className="min-w-0">
              <h2 className="font-heading font-semibold">{t("Basar")}</h2>
              <Link to={basarUrl(gift.basar_id)} className="text-sm underline underline-offset-4 hover:text-foreground/80 transition-colors duration-150 inline-flex items-center gap-1.5" data-testid="basar-gift-basar-link"><Gift className="w-4 h-4" /> {gift.basar_name}</Link>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {gift.organizer_name}</p>
            </div>
            {(basar.draw_at || basar.draw_info || basar.draw_place) && (
              <div className="text-sm text-muted-foreground space-y-1" data-testid="basar-gift-draw-info">
                {(basar.draw_at || basar.draw_info) && <p className="inline-flex items-start gap-2"><CalendarClock className="w-4 h-4 mt-0.5 shrink-0" /> {basar.draw_at ? t("Trekning {date}", { date: drawDateTime(basar.draw_at) }) : basar.draw_info}</p>}
                {basar.draw_place && <p className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /> {basar.draw_place}</p>}
              </div>
            )}
            {gift.description && <p className="text-sm text-muted-foreground whitespace-pre-line" data-testid="basar-gift-description">{gift.description}</p>}
            <div className="grid gap-2 sm:grid-cols-2 text-sm">
              <p className="flex items-start gap-2"><Smartphone className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" /><span><span className="block font-medium" data-testid="basar-gift-donor-name">{gift.donor_name}</span>{gift.phone_masked}</span></p>
              {gift.donor_email && <p className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" /><span className="break-all">{gift.donor_email}{done && emailEnabled ? <span className="block text-xs text-muted-foreground">{t("Bekreftelse er sendt på e-post.")}</span> : null}</span></p>}
            </div>
            {basar.contact_info && <p className="text-sm text-muted-foreground inline-flex items-start gap-2" data-testid="basar-gift-contact"><Phone className="w-4 h-4 mt-0.5 shrink-0" /> {basar.contact_info}</p>}
          </CardContent>
        </Card>

        <div className="space-y-3">
          <p className="text-xs text-muted-foreground" data-testid="basar-gift-seller-note">{t("Basaren arrangeres av {org}. Right in formidler bare gaven – pengegaver går direkte til arrangøren.", { org: gift.organizer_name })}</p>
          <Button asChild variant="ghost" size="sm" data-testid="basar-gift-back-link"><Link to={basarUrl(gift.basar_id)}><ArrowLeft className="w-4 h-4 mr-1.5" /> {t("Tilbake til basaren")}</Link></Button>
        </div>
      </div>
    </PublicLayout>
  );
}
